// Объекты месяца: где ведомость уже есть, где её ещё надо завести.
//
// Вынесено из разметки, чтобы считать ОДИН раз и одинаково в трёх местах: панель «Создать
// ведомость» (список с галочками), подвал списка ведомостей («Объектов без ведомости: 61,
// создать») и подтверждение «на все объекты». Три места, посчитавшие это по-своему, однажды
// назвали бы три разных числа, и человек перестал бы верить всем трём.
//
// ## Откуда числа
//
// Объекты — справочник действующих (`GET /sites?active=true`), ведомости — реестр месяца
// БЕЗ отборов. Реестр с отбором по состоянию или юрлицу показал бы «без ведомости» там, где она
// есть, просто не подошла под отбор, и создание такого объекта сервер бы отклонил.
//
// ## Чего здесь нет
//
// Запросов. Справочник и реестр приезжают хуками в самих экранах: права на них разные
// (`core.dictionary.view` и `core.payroll.view`), и у кого-то справочника нет вовсе — тогда
// число неизвестно, и честный ответ «не видно», а не ноль.

import type { SiteOut } from '@/shared/api/endpoints/org';
import type { PayrollOut } from '@/shared/api/endpoints/payroll';
import { formatPeriodKey, type Period } from '@/shared/time/period';

/** Объект, у которого ведомость за месяц уже есть, вместе с ней. */
export interface ОбъектСВедомостью {
  объект: SiteOut;
  ведомость: PayrollOut;
}

export interface СоставОбъектов {
  /** Кому ведомость ещё нужна: они и показываются галочками. */
  безВедомости: SiteOut[];
  /** У кого она уже есть: серым, со ссылкой «Открыть». */
  сВедомостью: ОбъектСВедомостью[];
}

function поНазванию(а: SiteOut, б: SiteOut): number {
  return а.name.localeCompare(б.name, 'ru');
}

/**
 * Разложить действующие объекты на «без ведомости» и «с ведомостью».
 *
 * Административная ведомость в счёт не идёт: у неё нет объекта (сценарий 38). Если у объекта
 * за месяц несколько ведомостей (два юрлица), берётся первая: открывать человеку достаточно
 * одну, а создавать вторую сервер всё равно не станет.
 */
export function разделитьОбъекты(
  объекты: readonly SiteOut[],
  ведомости: readonly PayrollOut[],
): СоставОбъектов {
  const поОбъекту = new Map<string, PayrollOut>();
  for (const ведомость of ведомости) {
    if (ведомость.site_id !== null && !поОбъекту.has(ведомость.site_id)) {
      поОбъекту.set(ведомость.site_id, ведомость);
    }
  }
  const безВедомости: SiteOut[] = [];
  const сВедомостью: ОбъектСВедомостью[] = [];
  for (const объект of [...объекты].sort(поНазванию)) {
    const ведомость = поОбъекту.get(объект.id);
    if (ведомость === undefined) {
      безВедомости.push(объект);
    } else {
      сВедомостью.push({ объект, ведомость });
    }
  }
  return { безВедомости, сВедомостью };
}

/**
 * Запрос для поиска по названию: без регистра и без различия «е» и «ё».
 *
 * «Щелково» набирают чаще, чем «Щёлково», и человек не обязан помнить, как записано у нас.
 */
export function ключПоиска(текст: string): string {
  return текст.trim().toLocaleLowerCase('ru').replaceAll('ё', 'е');
}

/** Подходит ли название под уже приведённый запрос. Пустой запрос подходит всему. */
export function названиеПодходит(название: string, запрос: string): boolean {
  return запрос === '' || ключПоиска(название).includes(запрос);
}

/**
 * Отпечаток того, что именно создаётся: месяц и отсортированные объекты.
 *
 * К нему привязан КЛЮЧ ОПЕРАЦИИ. Сервер сверяет ключ вместе с содержимым запроса
 * (`services/idempotency.py`): тот же ключ с другим набором объектов он отклоняет ошибкой «эта
 * операция уже выполнялась, но с другими данными» — человек менял отметки после обрыва и
 * упирался бы в непонятный отказ. Поэтому другой набор — другой ключ; повтор того же набора
 * после обрыва идёт с прежним, и сервер возвращает прежний отчёт вместо второй пачки.
 *
 * `null` — «все объекты, которые видит сервер»: единственный путь, где список объектов не
 * называется явно (человеку справочник не показывается).
 */
export function отпечатокСоздания(период: Period, объекты: readonly string[] | null): string {
  const месяц = formatPeriodKey(период);
  return объекты === null ? `${месяц}|все` : `${месяц}|${[...объекты].sort().join(',')}`;
}

/**
 * Есть ли в ведомости деньги к выплате: расчёт проводился и итог не ноль.
 *
 * Признаки — те, что уже стоят в строке списка: «считать некого» и «ещё не считали» приходят
 * с сервера, в браузере они не выводятся (`calculated_employees`). Ведомость без денег — это всё
 * остальное: считать некого, не считали или посчитали в ноль.
 */
export function естьДеньги(ведомость: PayrollOut): boolean {
  return ведомость.calculated_employees > 0 && ведомость.totals.total_payout > 0;
}
